// The Coordinator Workbench's, the scheduling request's and the scheduling pages' own words (lane B5 S11, S12). Every entry quotes his glossary or
// the design system's excerpts; the D0 label suites discover this module (a lane folder of src/views) and hold it to the
// same rules as src/mc. Words the design system already defines (the eight statuses, Ready to Schedule, Blocked for
// panel/coverage, the scheduling verbs, Conflict · OOF · working hours · time zone · room, Sync failure, delivered,
// Correlation ID, Responses, Original · Revised · Reason, To schedule, Scheduling SLA risk, Nothing needs action) are
// imported from it, never redefined here.

import { defineLabels, template } from "@/mc/labels";

const TEMPLATES = "\"Meeting templates: intake, recruiter screen, HM interview, Final Interview Loop, debrief and offer call.\"";
const OFFER_CALL = "Meeting template \"offer call\".";
const WORKBENCH =
  "\"4.2 Coordinator Workbench — Intakes, recruiter screens, HM interviews, Final Interview Loops and debriefs to schedule. Panel acceptance/declines, reschedules, travel, rooms, Teams links, conflicts and scheduling SLA risk.\"";
const JOURNEY = "\"Propose slots -> Availability -> Create Outlook event + Teams link\".";
const SEQUENCE = "\"Prevent invalid sequence.\"";
const REMIND = "| Remind | \"Remind HM when selection is due.\"";

/** The six meeting templates (G §9), as a row's title and the Schedule dialog's template. */
export const MEETING_TEMPLATE_LABELS = /* @__PURE__ */ defineLabels({
  intake: { text: "Intake" },
  recruiterScreen: { text: "Recruiter screen" },
  hmInterview: { text: "HM interview" },
  finalInterviewLoop: { text: "Final Interview Loop" },
  debrief: { text: "Debrief" },
  offerCall: { text: "Offer call" },
});

/** The Workbench's kind chips, in his §4.2 words (G §2), and its state chip. */
export const WORKBENCH_CHIP_LABELS = /* @__PURE__ */ defineLabels({
  meetingTemplate: { text: "Meeting template" },
  intakes: { text: "Intakes" },
  recruiterScreens: { text: "recruiter screens" },
  hmInterviews: { text: "HM interviews" },
  finalInterviewLoops: { text: "Final Interview Loops" },
  debriefs: { text: "debriefs" },
  offerCall: { text: "offer call" },
  reschedules: { text: "reschedules" },
});

/** The scheduling request's journey (Spec 5.2, G §17), drawn on the slide-over. */
export const SCHEDULING_JOURNEY_LABELS = /* @__PURE__ */ defineLabels({
  availability: { text: "Availability" },
  createEvent: { text: "Create Outlook event + Teams link" },
});

/** A conflict the engine finds that no other word names, and the manual nudge (G §17 *Remind*, his since the 7 Oct glossary). */
export const SCHEDULING_WORDS = /* @__PURE__ */ defineLabels({
  invalidSequence: { text: "invalid sequence" },
  remind: { text: "Remind" },
});

const MISSING = "\"missing controls are explicit.\"";
const SCORECARDS = "Do not finalize with missing scorecards.";
const COVERAGE = "\"Cannot schedule if required panel assignment is declined or coverage invalid.\"";

/** The debrief's scheduling card (S12, screens-walks §2.7): the readiness gate's missing controls, each named in his
 *  words with its round, value or seat as data. */
export const READINESS_LABELS = /* @__PURE__ */ defineLabels({
  missingControl: { text: "Missing control" },
  requiredAttendees: { text: "Required attendees" },
  scorecard: { text: "scorecard" },
  doNotFinalize: { text: "Do not finalize with missing scorecards." },
  coverage: { text: "coverage" },
  candidateSummary: { text: "Candidate summary" },
});

// ---- lane B13 (F-158, F-161, F-162, DC-231; DS DC-237, DC-240, DC-241) --------------------------------------------------

const HELD_OFFLINE =
  "The intake call's button reads **Call held offline — continue**; a Recruiter screen, an HM interview, a Final Interview Loop round and the debrief read **Held offline — continue**. The dialog asks **Held at** (a date and time, now unless changed), **Attendees** (filled in) and **Reason** (required), and commits with **Record and continue**; the meeting then reads **Held offline · {reason}** and the next part opens as a held meeting opens it.";
const HELD_OFFLINE_QE15 =
  "- Held offline (DC-237): *Held offline {date} — Part 2 is open.* · the timeline line *{actor} recorded the {meeting} as held offline · {reason}* · the notice *{actor} recorded the {meeting} as held offline — {title}* · the time error *Held at cannot be in the future*";
const RESCHEDULE_WORDS =
  "The control reads **Request a reschedule** (its icon beside the words, never alone); the dialog **Reschedule request**; the card's state line **Reschedule requested by {name} · {reason} · {when}**; the Coordinator's Workbench row and Action Center row **Reschedule request · {meeting} · {requisition id}**; the timeline line **Reschedule requested**.";
const COPY_TEAMS = "- the scheduling card's copy control in words (DC-241): *Copy Teams link*";
const HM_AVAILABILITY = "| HM availability | Tejesh 12 Oct 10:23:";

/** F-158 (DC-237): a meeting a step waits on is recorded held without a booked event, with a reason. */
export const HELD_OFFLINE_LABELS = /* @__PURE__ */ defineLabels({
  callHeldOfflineContinue: { text: "Call held offline — continue" },
  heldOfflineContinue: { text: "Held offline — continue" },
  /** The dialog's title: the button's words before the dash. */
  callHeldOffline: { text: "Call held offline" },
  heldOffline: { text: "Held offline" },
  heldAt: { text: "Held at" },
  recordAndContinue: { text: "Record and continue" },
  futureHeldAt: { text: "Held at cannot be in the future" },
});

/** The card's line once a meeting is held offline, and the intake call card's line (Q-E15). */
export const HELD_OFFLINE_TEMPLATES = {
  heldOfflineBecause: /* @__PURE__ */ template({ text: "Held offline · {reason}" }),
  heldOfflinePartTwo: /* @__PURE__ */ template({ text: "Held offline {date} — Part 2 is open." }),
};

/** F-162 (DC-241): the reschedule request in words, never a glyph alone. */
// G §9 "Meeting names on cards": the word a meeting carries on a card and in the Action Center titles the server builds
// ("Respond: Intake call · R-####", "Reschedule request · Intake call · R-####") — the intake's card name, where the
// Workbench's own template word is the shorter *Intake*.
const MEETING_NAMES = "| Meeting names on cards | **Intake call** · **Recruiter screen** · **HM interview** · **Final Interview Loop · Round {n}** (§23.8) · **Debrief** · **Offer call**";
export const MEETING_CARD_LABELS = /* @__PURE__ */ defineLabels({
  intakeCall: { text: "Intake call" },
});

export const RESCHEDULE_REQUEST_LABELS = /* @__PURE__ */ defineLabels({
  requestAReschedule: { text: "Request a reschedule" },
  rescheduleRequest: { text: "Reschedule request" },
  copyTeamsLink: { text: "Copy Teams link" },
});

export const RESCHEDULE_REQUEST_TEMPLATES = {
  requestedBy: /* @__PURE__ */ template({ text: "Reschedule requested by {name} · {reason} · {when}" }),
};

/** DC-231: the HM's windows head the intake call's proposals (G §6 *HM availability*). */
export const INTAKE_SCHEDULING_LABELS = /* @__PURE__ */ defineLabels({
  hmAvailability: { text: "HM availability" },
});
